/**
 * 💰 YACHT DESTINATION PRICING MANAGEMENT PAGE
 * Admin interface for managing yacht destination pricing for one-way and round-trip bookings
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  DirectionsBoat,
  FilterList,
  Search,
  AttachMoney,
  CheckCircle,
  Cancel,
  Place,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { yachtDestinationPricingService, YachtWithPricing } from '@/services/yachtDestinationPricingService';
import { yachtDestinationsService, YachtDestination } from '@/services/yachtDestinationsService';
import SetYachtPricingDialog from '@/components/pricing/SetYachtPricingDialog';
import ManageYachtPricingDialog from '@/components/pricing/ManageYachtPricingDialog';
import CreateYachtBookingDialog from '@/components/bookings/CreateYachtBookingDialog';

export const YachtDestinationPricingManagementPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [yachts, setYachts] = useState<YachtWithPricing[]>([]);
  const [destinations, setDestinations] = useState<YachtDestination[]>([]);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedDestination, setSelectedDestination] = useState<string>('');
  const [pricingFilter, setPricingFilter] = useState<'all' | 'with_pricing' | 'without_pricing'>('all');

  // Unique countries and cities for filters
  const [countries, setCountries] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);

  // Dialogs
  const [setPricingDialog, setSetPricingDialog] = useState<{ open: boolean; yacht: YachtWithPricing | null }>({
    open: false,
    yacht: null
  });
  const [managePricingDialog, setManagePricingDialog] = useState<{ open: boolean; yacht: YachtWithPricing | null }>({
    open: false,
    yacht: null
  });
  const [createBookingOpen, setCreateBookingOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      loadDestinationsForCountry(selectedCountry, selectedCity);
    } else {
      setSelectedDestination('');
    }
  }, [selectedCountry, selectedCity]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [yachtsResponse, destinationsResponse] = await Promise.all([
        yachtDestinationPricingService.getYachtsWithPricing(),
        yachtDestinationsService.getDestinations({ active: true }).catch(err => {
          return { success: false, error: 'Backend endpoint not implemented yet' };
        })
      ]);

      if (yachtsResponse.success && yachtsResponse.yachts) {
        setYachts(yachtsResponse.yachts);

        // Extract unique countries and cities
        const uniqueCountries = [...new Set(yachtsResponse.yachts.map(y => y.country).filter(Boolean))];
        const uniqueCities = [...new Set(yachtsResponse.yachts.map(y => y.city).filter(Boolean))];
        setCountries(uniqueCountries as string[]);
        setCities(uniqueCities as string[]);
      } else {
        toast.error(yachtsResponse.error || 'Failed to load yachts');
      }

      if (destinationsResponse.success && destinationsResponse.data?.destinations) {
        setDestinations(destinationsResponse.data.destinations);
      } else {
        // Don't show error for destinations - they might not be created yet
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadDestinationsForCountry = async (country: string, city?: string) => {
    try {
      const filters: any = { country, active: true };
      if (city) filters.city = city;

      const response = await yachtDestinationsService.getDestinations(filters).catch(err => {
        return { success: false, error: 'Backend endpoint not implemented yet' };
      });

      if (response.success && response.data?.destinations) {
        setDestinations(response.data.destinations);
      }
    } catch (error) {
    }
  };

  const handleSetPricing = (yacht: YachtWithPricing) => {
    setSetPricingDialog({ open: true, yacht });
  };

  const handleManagePricing = (yacht: YachtWithPricing) => {
    setManagePricingDialog({ open: true, yacht });
  };

  const handlePricingSet = () => {
    setSetPricingDialog({ open: false, yacht: null });
    loadData(); // Reload to show updated counts
  };

  const handlePricingUpdated = () => {
    setManagePricingDialog({ open: false, yacht: null });
    loadData(); // Reload to show updated data
  };

  // Filter yachts
  const filteredYachts = yachts.filter(yacht => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        yacht.yachtName.toLowerCase().includes(query) ||
        yacht.yachtType.toLowerCase().includes(query) ||
        yacht.ownerName?.toLowerCase().includes(query);

      if (!matchesSearch) return false;
    }

    // Pricing filter
    if (pricingFilter === 'with_pricing' && !yacht.hasDestinationPricing) return false;
    if (pricingFilter === 'without_pricing' && yacht.hasDestinationPricing) return false;

    // Location filters
    if (selectedCountry && yacht.country !== selectedCountry) return false;
    if (selectedCity && yacht.city !== selectedCity) return false;

    // Destination filter
    if (selectedDestination) {
      if (!yacht.pricings || yacht.pricings.length === 0) return false;
      const matchesDestination = yacht.pricings.some(p => p.destinationId === selectedDestination);
      return matchesDestination;
    }

    return true;
  });

  const formatCurrency = (amountInKobo: number) => {
    return `₦${(amountInKobo / 100).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsBoat /> Yacht Destination Pricing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage destination pricing for yachts across different coastal locations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateBookingOpen(true)}
        >
          Create Destination Trip
        </Button>
      </Box>

      {/* Stats Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Yachts
              </Typography>
              <Typography variant="h4">
                {yachts.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                With Destination Pricing
              </Typography>
              <Typography variant="h4" color="success.main">
                {yachts.filter(y => y.hasDestinationPricing).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Without Pricing
              </Typography>
              <Typography variant="h4" color="warning.main">
                {yachts.filter(y => !y.hasDestinationPricing).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Pricing Records
              </Typography>
              <Typography variant="h4">
                {yachts.reduce((sum, y) => sum + y.destinationPricingCount, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search yachts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Pricing Status</InputLabel>
                <Select
                  value={pricingFilter}
                  label="Pricing Status"
                  onChange={(e) => setPricingFilter(e.target.value as any)}
                >
                  <MenuItem value="all">All Yachts</MenuItem>
                  <MenuItem value="with_pricing">With Pricing</MenuItem>
                  <MenuItem value="without_pricing">Without Pricing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  value={selectedCountry}
                  label="Country"
                  onChange={(e) => {
                    setSelectedCountry(e.target.value);
                    setSelectedCity('');
                    setSelectedDestination('');
                  }}
                >
                  <MenuItem value="">All Countries</MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth disabled={!selectedCountry}>
                <InputLabel>City</InputLabel>
                <Select
                  value={selectedCity}
                  label="City"
                  onChange={(e) => {
                    setSelectedCity(e.target.value);
                    setSelectedDestination('');
                  }}
                >
                  <MenuItem value="">All Cities</MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth disabled={!selectedCountry}>
                <InputLabel>Destination</InputLabel>
                <Select
                  value={selectedDestination}
                  label="Destination"
                  onChange={(e) => setSelectedDestination(e.target.value)}
                >
                  <MenuItem value="">All Destinations</MenuItem>
                  {destinations.map((dest) => (
                    <MenuItem key={dest.id} value={dest.id}>
                      {dest.destinationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCountry('');
                  setSelectedCity('');
                  setSelectedDestination('');
                  setPricingFilter('all');
                }}
                sx={{ height: '56px' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Yachts Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredYachts.length === 0 ? (
            <Alert severity="info">
              No yachts found matching the selected filters.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Yacht</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Capacity</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell align="center">Pricing Status</TableCell>
                    <TableCell align="center">Destinations</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredYachts.map((yacht) => (
                    <TableRow key={yacht.yachtId} hover>
                      <TableCell>
                        <Stack>
                          <Typography variant="body2" fontWeight={500}>
                            {yacht.yachtName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Owner: {yacht.ownerName || 'Unknown'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {yacht.yachtType}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {yacht.capacity} passengers
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack>
                          <Typography variant="body2">
                            {yacht.city || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {yacht.country || 'N/A'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        {yacht.hasDestinationPricing ? (
                          <Chip
                            icon={<CheckCircle />}
                            label="Configured"
                            size="small"
                            color="success"
                          />
                        ) : (
                          <Chip
                            icon={<Cancel />}
                            label="Not Set"
                            size="small"
                            color="warning"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={yacht.destinationPricingCount || 0}
                          size="small"
                          color={yacht.destinationPricingCount > 0 ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {yacht.hasDestinationPricing ? (
                            <Tooltip title="Manage Pricing">
                              <IconButton
                                size="small"
                                onClick={() => handleManagePricing(yacht)}
                                color="primary"
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Set Pricing">
                              <IconButton
                                size="small"
                                onClick={() => handleSetPricing(yacht)}
                                color="success"
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Set Pricing Dialog */}
      {setPricingDialog.yacht && (
        <SetYachtPricingDialog
          open={setPricingDialog.open}
          yacht={setPricingDialog.yacht}
          destinations={destinations}
          onClose={() => setSetPricingDialog({ open: false, yacht: null })}
          onSuccess={handlePricingSet}
        />
      )}

      {/* Manage Pricing Dialog */}
      {managePricingDialog.yacht && (
        <ManageYachtPricingDialog
          open={managePricingDialog.open}
          yacht={managePricingDialog.yacht}
          onClose={() => setManagePricingDialog({ open: false, yacht: null })}
          onSuccess={handlePricingUpdated}
        />
      )}

      {/* Create Yacht Destination Booking Dialog */}
      <CreateYachtBookingDialog
        open={createBookingOpen}
        onClose={() => setCreateBookingOpen(false)}
        onSuccess={() => {
          setCreateBookingOpen(false);
          loadData(); // Refresh data after successful booking
        }}
        initialBookingType="destination"
      />
    </Box>
  );
};

export default YachtDestinationPricingManagementPage;
